import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Headset, Plus, Tags, Loader2, AlertTriangle, Video } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { apiErr } from "../../../lib/apiErr";
import { PageHeader } from "../../../components/PageHeader";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { AgentRow } from "./AgentRow";
import { RolesDialog } from "./RolesDialog";
import { PROV, toBody, useRoles } from "./agentUtil";

export default function PlatformSupportAgents() {
  const { role } = useOutletContext() || {};
  const readOnly = role !== "super_admin";
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [rt, setRt] = useState(null);
  const [busy, setBusy] = useState("");
  const [askDel, setAskDel] = useState(null);
  const [rolesOpen, setRolesOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [roles, reloadRoles] = useRoles();
  const load = useCallback(() => api.get("/platform/support-agents").then((r) => setD(r.data)).catch((e) => { toast.error(apiErr(e, "Gagal memuat agent")); setD({ items: [] }); }), []);
  useEffect(() => { load(); api.get("/platform/models/realtime-avatar").then((r) => setRt(r.data)).catch(() => setRt(null)); }, [load]);

  const run = async (key, fn, msg) => { setBusy(key); try { await fn(); if (msg) toast.success(msg); await load(); reloadRoles(); } catch (e) { toast.error(apiErr(e, "Gagal")); } finally { setBusy(""); } };
  const saveOrder = (items) => { setD((x) => ({ ...x, items })); run("__order", () => api.put("/platform/support-agents/order", { ids: items.map((a) => a.id) }), "Urutan disimpan"); };
  const move = (i, dir) => { const items = [...d.items]; const j = i + dir; [items[i], items[j]] = [items[j], items[i]]; saveOrder(items); };
  const drop = (to) => { if (dragIdx == null || dragIdx === to) return; const items = [...d.items]; const [m] = items.splice(dragIdx, 1); items.splice(to, 0, m); saveOrder(items); };
  const dragProps = (i) => readOnly ? {} : {
    onDragStart: () => setDragIdx(i), onDragEnd: () => { setDragIdx(null); setOverIdx(null); },
    onDragOver: (e) => { e.preventDefault(); setOverIdx(i); }, onDrop: (e) => { e.preventDefault(); drop(i); setDragIdx(null); setOverIdx(null); },
    className: `${dragIdx === i ? "opacity-40" : ""} ${overIdx === i && dragIdx !== i ? "bg-brand-soft" : ""}`,
  };
  const roleOf = (k) => (roles || []).find((r) => r.key === k);
  const provider = PROV[d?.active_provider] || d?.active_provider;
  const runway = rt?.providers?.runway;

  return (
    <div data-testid="support-agents-page">
      <PageHeader icon={Headset} title="Support Agents" description="Kelola agent bantuan yang tampil di Beranda & Chat pengguna: nama, peran, persona, knowledge, model, suara, dan avatar video.">
        {!readOnly && <button onClick={() => setRolesOpen(true)} className="btn-soft inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold" data-testid="agents-manage-roles"><Tags size={14} /> Kelola Peran</button>}
        {!readOnly && <button onClick={() => nav("new")} className="btn-grad inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold" data-testid="agents-new"><Plus size={14} /> Agent Baru</button>}
      </PageHeader>
      {d && (
        <div className={`mt-5 flex flex-wrap items-center gap-2 rounded-xl px-4 py-3 text-xs ${d.video_paused ? "bg-amber-50 text-amber-800" : "bg-slate-50 text-slate-600"}`} data-testid="agents-provider-banner">
          {d.video_paused ? <AlertTriangle size={14} /> : <Video size={14} className="text-brand" />}
          Provider avatar aktif: <b>{provider}</b>
          {d.active_provider === "runway" && runway?.balance_known && <span>· Saldo <b>{Number(runway.balance).toLocaleString("id-ID")}</b> kredit</span>}
          {d.video_paused && <span className="font-semibold">· Video dijeda otomatis (saldo di bawah ambang {runway?.min_credits ?? "—"} kredit)</span>}
          <span className="ml-auto text-slate-400">Ubah di Model AI › Realtime Avatar</span>
        </div>
      )}
      {readOnly && <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700" data-testid="agents-readonly">Mode baca — hanya Super Admin yang dapat mengubah agent.</p>}
      {!d ? <Loader2 className="mt-6 animate-spin text-slate-400" /> : !d.items.length ? (
        <div className="aivora-card mt-5 px-4 py-10 text-center text-sm text-slate-500" data-testid="agents-empty">Belum ada agent.</div>
      ) : (
        <ul className="aivora-card mt-5 divide-y divide-slate-100 overflow-hidden" data-testid="agents-list">
          {d.items.map((a, i) => (
            <AgentRow key={a.id} a={a} idx={i} total={d.items.length} role={roleOf(a.role_key)} provider={provider} readOnly={readOnly} busy={busy === a.id || busy === "__order"} drag={dragProps(i)}
              onMove={move} onEdit={() => nav(a.id)}
              onDuplicate={() => run(a.id, () => api.post(`/platform/support-agents/${a.id}/duplicate`), `${a.name} diduplikat (nonaktif)`)}
              onDefault={() => run(a.id, () => api.post(`/platform/support-agents/${a.id}/default`), `${a.name} sekarang default`)}
              onDelete={() => setAskDel(a)}
              onToggle={() => run(a.id, () => api.put(`/platform/support-agents/${a.id}`, { ...toBody(a), enabled: !a.enabled }), a.enabled ? `${a.name} dinonaktifkan` : `${a.name} diaktifkan`)} />
          ))}
        </ul>
      )}
      <ConfirmDialog open={!!askDel} testid="agent-delete-confirm" title={`Hapus ${askDel?.name || "agent"}?`} confirmLabel="Ya, hapus"
        message="Percakapan user dengan agent ini akan ditutup dan disembunyikan. Dokumen knowledge agent tidak dipakai lagi."
        onCancel={() => setAskDel(null)} onConfirm={() => { const a = askDel; setAskDel(null); run(a.id, () => api.delete(`/platform/support-agents/${a.id}`), `${a.name} dihapus`); }} />
      {rolesOpen && <RolesDialog roles={roles || []} onClose={() => setRolesOpen(false)} onChanged={() => { reloadRoles(); load(); }} />}
    </div>
  );
}
